import type { Coordinate } from 'ol/coordinate';
import { Test } from '../core/test';
import { GVAbstractTester } from './abstract-gv-tester';
import { DomHelper } from 'geoview-core/core/utils/dom-helper';
import { getStoreUIActiveFooterBarTab } from 'geoview-core/core/stores/states/ui-state';
import { getStoreGeochartChartsConfig, getStoreGeochartSelectedLayerPath } from 'geoview-core/core/stores/states/geochart-state';
import type { AbstractGVLayer } from 'geoview-core/geo/layer/gv-layers/abstract-gv-layer';

/**
 * Main Map testing class.
 */
export class GeochartTester extends GVAbstractTester {
  /**
   * Returns the name of the Tester.
   *
   * @returns The name of the Tester
   */
  override getName(): string {
    return 'GeochartTester';
  }

  /**
   * Tests opening the Geochart for a given layer path.
   *
   * @param layerPath - The layer path of the layer
   * @param lonlat - The coordinate on the map to query
   * @returns A promise that resolves when the test completes
   */
  testGeochartOpenForLayerMapClick(layerPath: string, lonlat: Coordinate): Promise<Test<AbstractGVLayer>> {
    // Test
    return this.test(
      `Test Geochart on layer ${layerPath}...`,
      (test) => {
        // Continue the test and return the layer
        return this.helperStepLayerWithGeochart(test, layerPath, lonlat);
      },
      (test) => {
        // Perform assertions
        // Check that geochart is the active footer bar
        test.addStep("Verifying 'geochart' is the selected footer tab...");
        Test.assertIsEqual(getStoreUIActiveFooterBarTab(this.getMapId()).tabId, 'geochart');

        // Check that layer path is selected
        test.addStep(`Verifying ${layerPath} is the selected layer for the geochart...`);
        Test.assertIsEqual(getStoreGeochartSelectedLayerPath(this.getMapId()), layerPath);
      }
    );
  }

  /**
   * Tests adding a the Airborne Geocore layer, launching a query layers at a given lonlat, set the active footerbar tab to geochart and select the layer in the geochart panel.
   *
   * @returns A promise that resolves when the test completes.
   */
  testAddGeocoreLayerUUIDForGeochartAirborne(): Promise<Test<AbstractGVLayer>> {
    // Test it
    return this.testAddGeocoreLayerUUIDForGeochart(
      GVAbstractTester.AIRBORNE_RADIOACTIVITY_UUID,
      GVAbstractTester.AIRBORNE_RADIOACTIVITY_UUID_WITH_SUFFIX,
      GVAbstractTester.AIRBORNE_RADIOACTIVITY_GROUP,
      GVAbstractTester.OTTAWA_LONLAT,
      {
        [GVAbstractTester.AIRBORNE_RADIOACTIVITY_UUID_WITH_SUFFIX]: {
          layers: [
            {
              layerId: GVAbstractTester.AIRBORNE_RADIOACTIVITY_UUID_WITH_SUFFIX,
              propertyValue: 'OBJECTID',
              propertyDisplay: 'Location_Emplacement',
            },
          ],
          chart: 'line',
          query: {
            type: 'esriRegular',
            url: 'https://maps-cartes.services.geo.ca/server_serveur/rest/services/HC/airborne_radioactivity_en/MapServer/3',
            queryOptions: {
              whereClauses: [
                {
                  field: 'Location_Emplacement',
                  prefix: "'",
                  valueFrom: 'Location_Emplacement',
                  suffix: "'",
                },
              ],
              orderByField: 'CollectionStart_DebutPrelevement',
            },
          },
        },
      }
    );
  }

  /**
   * Tests adding a Geocore layer, launching a query layers at a given lonlat, set the active footerbar tab to geochart and select the layer in the geochart panel.
   *
   * @param uuid - The uuid of the Gecoore layer
   * @param layerPathAdd - The layer path of the layer that was added
   * @param layerPathRemove - The layer path of the layer to remove once the test terminates
   * @param lonlat - The coordinate on the map to query
   * @param expectedGeochartChartsConfig - The expected geochart charts configuration to validate
   */
  testAddGeocoreLayerUUIDForGeochart(
    uuid: string,
    layerPathAdd: string,
    layerPathRemove: string,
    lonlat: Coordinate,
    expectedGeochartChartsConfig: Record<string, unknown>
  ): Promise<Test<AbstractGVLayer>> {
    // Test
    return this.test(
      'Test Geochart',
      async (test) => {
        // Update the step
        test.addStep(`Adding the layer on the map for ${uuid}`);

        // Add the geoview layer by geocore uuid
        const result = await this.getControllersRegistry().layerCreatorController.addGeoviewLayerByGeoCoreUUID(uuid);

        // Update the step
        test.addStep(`Adding the layer on the map...`);

        // Wait for the layer to be processed (bit optional)
        await result?.promiseLayer;

        // Continue the test and return the layer
        return this.helperStepLayerWithGeochart(test, layerPathAdd, lonlat);
      },
      (test) => {
        // Perform assertions
        test.addStep('Verifying expected geochart config...');
        const geochartsConfig = getStoreGeochartChartsConfig(this.getMapId());
        Test.assertJsonObject(geochartsConfig, expectedGeochartChartsConfig);

        // Check that geochart is the active footer bar
        test.addStep("Verifying 'geochart' is the selected footer tab...");
        Test.assertIsEqual(getStoreUIActiveFooterBarTab(this.getMapId()).tabId, 'geochart');

        // Check that layer path is selected
        test.addStep(`Verifying ${layerPathAdd} is the selected layer for the geochart...`);
        Test.assertIsEqual(getStoreGeochartSelectedLayerPath(this.getMapId()), layerPathAdd);
      },
      (test) => {
        // Redirect to LayerTest to help test the removal of the layer
        this.finalizeStepRemoveLayerAndAssert(test, layerPathRemove);
      }
    );
  }

  /**
   * Retrieves a layer from the map, performs a feature info query at a specific coordinate,
   * and sets up the geochart UI with that layer. Steps are logged to the provided test instance.
   *
   * @template T - The type parameter for the test instance
   * @param test - The test instance used to log each step of the geochart setup process
   * @param mapViewer - The map viewer containing the layer and UI context
   * @param layerPath - The unique path or ID of the layer to interact with
   * @param lonlat - The longitude/latitude coordinate at which to query the layer
   * @returns A promise that resolves to the layer after setup is complete
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path
   * @throws {LayerWrongTypeError} When the layer is of wrong type at the given layer path
   */
  async helperStepLayerWithGeochart<T>(test: Test<T>, layerPath: string, lonlat: Coordinate): Promise<AbstractGVLayer> {
    // Get the layer
    test.addStep(`Getting the layer with the geochart ${layerPath}...`);
    const layer = this.getControllersRegistry().layerController.getGeoviewLayerRegular(layerPath);

    // Wait until the layer has at least loaded once
    test.addStep(`Waiting for its layer 'loaded' status...`);
    await layer.waitForLoadedOnce();

    // Perform a map click using the feature info layer set
    test.addStep(`Perform query operation at given coordinates...`);
    await this.getControllersRegistry().layerSetController.queryAtLonLat(lonlat);

    // Set the footer tab to Geochart
    test.addStep(`Setting active footerbar tab to geochart and wait for UI to refresh...`);
    this.getControllersRegistry().uiController.setActiveFooterBarTab('geochart');
    await DomHelper.waitForFooterTabSelected(this.getMapId(), 'geochart');

    // Select the right layer path
    test.addStep(`Selecting the geochart for the added layer...`);
    this.getControllersRegistry().geoChartController?.setSelectedLayerPath(layerPath);

    // Wait for fun so that we can see the actual chart in the UI before terminating the test
    await GVAbstractTester.waitForFun();

    // Return the layer
    return layer;
  }
}
